/**
 * The `+ CUSTOM VIDEOS` composer (W3's last piece) — an overlay rather than the
 * inline row it replaces: centred on desktop, a bottom sheet at phone width.
 *
 * IT STAYS OPEN AFTER EACH ADD. That is the point of the change: pasting three
 * clips used to mean opening the form three times, and the list below the field
 * is what makes a second paste obviously possible.
 *
 * ATTRIBUTION MOVED HERE, AND THAT IS A REAL LOSS. The carousel's stage used to
 * carry `<title> · ADDED BY <NAME> · <AGO>` under every crew video, always
 * visible. The design removes that row and puts the same facts in this list, so
 * seeing who added something now costs a click. Decided 2026-08-13 (Marcus):
 * match the design, information loss included — recorded so it reads as a
 * decision rather than as a caption somebody forgot to port.
 *
 * The bad-link message is INLINE and not a toast, because it belongs to the
 * field that caused it and the field is still on screen. `addVideo` still owns
 * every other message; this one only fires when the link cannot parse at all,
 * which the client can tell without asking the server.
 */

import { useState } from 'react';

import { parseVideoUrl, VIDEO_TITLE_MAX, VIDEO_URL_MAX } from '../lib/videoUrl.ts';
import { useQd } from '../store/index.ts';
import { mediaVM } from '../store/selectors.ts';
import { useEscape } from '../desktop/components/Modal.tsx';
import type { GameId } from '../lib/types.ts';
import type { CSSProperties, FormEvent } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

const INPUT: CSSProperties = {
  boxSizing: 'border-box',
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: 11,
  padding: '10px 11px',
  outline: 'none',
};

export function VideoComposer({
  gameId,
  desktop,
  onClose,
}: {
  gameId: GameId;
  desktop?: boolean | undefined;
  onClose: () => void;
}): JSX.Element {
  const s = useQd();
  const vm = mediaVM(s, gameId);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [err, setErr] = useState(false);

  // The one Escape stack, shared with every modal — a second `keydown` listener
  // would race it on the same key. See `useEscape`.
  useEscape(onClose);

  // Only the crew's own videos. Steam trailers and screenshots are not ours to
  // list here, and neither can be removed.
  const mine = vm.items.filter((it) => it.kind === 'yt' || it.kind === 'tw');

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const ok = !!parseVideoUrl(url);
    setErr(!ok);
    // The store still owns every server-side message; a link that will be
    // rejected keeps what was pasted so it can be corrected in place.
    void s.addVideo(gameId, url, title);
    if (!ok) return;
    setUrl('');
    setTitle('');
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'var(--gt-overlay)',
        display: 'flex',
        alignItems: desktop ? 'center' : 'flex-end',
        justifyContent: 'center',
        padding: desktop ? 24 : 0,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-screen-label="Custom videos"
        style={{
          width: desktop ? 520 : '100%',
          maxWidth: desktop ? '100%' : 430,
          maxHeight: '86vh',
          background: 'var(--gt-card)',
          border: '1px solid var(--gt-border)',
          boxShadow: desktop ? 'var(--gt-shadow-modal)' : '0 -10px 0 rgba(0,0,0,.35)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '13px 12px 13px 16px',
            borderBottom: '1px solid var(--gt-border)',
            flex: 'none',
          }}
        >
          <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--acc)', transform: 'translateY(0.13em)' }}>
            CUSTOM VIDEOS · {mine.length}
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="CLOSE"
            style={{
              background: 'none',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-5)',
              fontFamily: MONO,
              fontSize: 12,
              lineHeight: 1,
              padding: desktop ? '9px 11px' : '13px 15px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <form
            onSubmit={submit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 14,
              borderBottom: '1px solid var(--gt-line)',
              flex: 'none',
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (err) setErr(false);
                }}
                placeholder="PASTE A YOUTUBE OR TWITCH LINK"
                maxLength={VIDEO_URL_MAX}
                aria-label="YOUTUBE OR TWITCH LINK"
                style={{ ...INPUT, flex: '3 1 200px', borderColor: err ? 'var(--acc2)' : 'var(--gt-border-2)' }}
              />
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="TITLE (OPTIONAL)"
                maxLength={VIDEO_TITLE_MAX}
                aria-label="VIDEO TITLE (OPTIONAL)"
                style={{ ...INPUT, flex: '2 1 140px' }}
              />
            </div>
            {err ? (
              <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.05em', color: 'var(--acc2)' }}>
                ✕ NOT A YOUTUBE OR TWITCH LINK
              </span>
            ) : null}
            {/* Enter submits by virtue of being a real form with a submit
                button — no key handler, so the browser's own behaviour holds. */}
            <button
              type="submit"
              style={{
                alignSelf: 'flex-start',
                background: 'var(--accSoft)',
                border: '1px solid var(--acc)',
                color: 'var(--acc)',
                fontFamily: PIXEL,
                fontSize: 8,
                padding: desktop ? '11px 14px' : '15px 16px',
                boxShadow: '3px 3px 0 rgba(0,0,0,.5)',
                cursor: 'pointer',
              }}
            >
              ADD ▸
            </button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {mine.map((it) => (
              <div
                key={it.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '11px 14px',
                  borderBottom: '1px solid var(--gt-line-0)',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    fontFamily: MONO,
                    fontSize: 8,
                    letterSpacing: '.08em',
                    color: 'var(--gt-muted)',
                    border: '1px solid var(--gt-border-2)',
                    padding: '3px 4px',
                  }}
                >
                  {it.kind === 'tw' ? 'TW' : 'YT'}
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {it.title ? (
                    <span
                      style={{
                        fontFamily: MONO,
                        fontSize: 10.5,
                        color: 'var(--gt-text-2)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.title}
                    </span>
                  ) : null}
                  {/* The attribution the stage caption used to carry. */}
                  <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-muted-2)' }}>
                    {'byTxt' in it ? it.byTxt : ''}
                  </span>
                </span>
                {it.canDel ? (
                  <button
                    type="button"
                    onClick={() => void s.rmVideo(it.id)}
                    style={{
                      flex: 'none',
                      background: 'none',
                      border: '1px solid var(--acc2)',
                      color: 'var(--acc2)',
                      fontFamily: MONO,
                      fontSize: 9.5,
                      padding: desktop ? '5px 7px' : '9px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ REMOVE
                  </button>
                ) : null}
              </div>
            ))}
            {!mine.length ? (
              <div
                style={{
                  padding: '18px 14px',
                  fontFamily: MONO,
                  fontSize: 9.5,
                  lineHeight: 1.8,
                  color: 'var(--gt-muted-2)',
                  textAlign: 'center',
                }}
              >
                NOBODY HAS ADDED ONE YET
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ padding: 14, borderTop: '1px solid var(--gt-line)', flex: 'none' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              background: 'none',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text-3)',
              fontFamily: MONO,
              fontSize: 10.5,
              padding: desktop ? '11px 12px' : '15px 12px',
              cursor: 'pointer',
            }}
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}
